import { supabase } from '../lib/supabase';
import { analyticsService } from './analyticsService';


export const dashboardService = {
  /**
   * Get dashboard metrics for the user
   */
  async getMetrics(brandId = null) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get brands to use
      let activeBrand = brandId;
      if (!activeBrand) {
        const { data: brands, error: brandError } = await supabase
          ?.from('brands')
          ?.select('id')
          ?.eq('user_id', user?.id)
          ?.eq('is_active', true)
          ?.limit(1)
          ?.maybeSingle();
        
        if (!brandError && brands) {
          activeBrand = brands?.id;
        }
      }

      if (!activeBrand) {
        return {
          totalFollowers: 0,
          followerChange: 0,
          engagementRate: 0,
          engagementChange: 0,
          scheduledPosts: 0,
          scheduledChange: 0,
          activeCampaigns: 0,
          campaignChange: 0
        };
      }

      // Get follower count and growth
      const { data: followerData } = await supabase?.from('follower_growth')?.select('follower_count, growth_percentage')?.eq('brand_id', activeBrand)?.order('recorded_at', { ascending: false })?.limit(1)?.single();

      // Get engagement metrics
      const metrics = await analyticsService?.getAggregatedMetrics(activeBrand);

      // Get scheduled posts count
      const { count: scheduledCount } = await supabase?.from('scheduled_posts')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.in('post_status', ['scheduled', 'draft']);

      // Get active campaigns count
      const { count: campaignsCount } = await supabase?.from('campaigns')?.select('*', { count: 'exact', head: true })?.eq('user_id', user?.id)?.eq('status', 'active');

      return {
        totalFollowers: followerData?.follower_count || 0,
        followerChange: followerData?.growth_percentage || 0,
        engagementRate: metrics?.avgEngagementRate || 0,
        engagementChange: 2.3, // Calculate from historical data
        scheduledPosts: scheduledCount || 0,
        scheduledChange: 8, // Calculate from historical data
        activeCampaigns: campaignsCount || 0,
        campaignChange: -2 // Calculate from historical data
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  /**
   * Get recent activity for the dashboard
   */
  async getRecentActivity(limit = 10) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get recent posts with analytics
      const { data: recentPosts } = await supabase?.from('post_analytics')?.select(`
          id,
          platform,
          likes,
          comments,
          shares,
          recorded_at,
          post:scheduled_posts(id, content, media_url, media_alt, published_at)
        `)?.eq('post.user_id', user?.id)?.order('recorded_at', { ascending: false })?.limit(limit);

      // Get recent comments
      const { data: recentComments } = await supabase?.from('comment_queue')?.select('id, target_platform, comment_text, scheduled_time, status')?.eq('user_id', user?.id)?.eq('status', 'posted')?.order('scheduled_time', { ascending: false })?.limit(limit);

      // Combine and format activities
      const activities = [];

      // Add posts
      recentPosts?.forEach(item => {
        activities?.push({
          id: `post-${item?.id}`,
          type: 'post',
          platform: item?.platform,
          content: item?.post?.content,
          timestamp: item?.recorded_at,
          thumbnail: item?.post?.media_url,
          thumbnailAlt: item?.post?.media_alt,
          engagement: {
            likes: item?.likes,
            comments: item?.comments,
            shares: item?.shares
          }
        });
      });

      // Add comments
      recentComments?.forEach(item => {
        activities?.push({
          id: `comment-${item?.id}`,
          type: 'comment',
          platform: item?.target_platform,
          content: item?.comment_text,
          timestamp: item?.scheduled_time,
          engagement: {
            likes: 0,
            comments: 0,
            shares: 0
          }
        });
      });

      // Sort by timestamp and limit
      return activities
        ?.sort((a, b) => new Date(b?.timestamp) - new Date(a?.timestamp))
        ?.slice(0, limit)
        ?.map(activity => ({
          ...activity,
          timestamp: this?.formatTimestamp(activity?.timestamp)
        }));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      throw error;
    }
  },

  /**
   * Get growth chart data
   */
  async getGrowthData(brandId = null, days = 7) {
    try {
      if (!supabase) throw new Error('Supabase client not initialized');
      
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get brands to use
      let activeBrand = brandId;
      if (!activeBrand) {
        const { data: brands } = await supabase?.from('brands')?.select('id')?.eq('user_id', user?.id)?.eq('is_active', true)?.limit(1)?.maybeSingle();
        activeBrand = brands?.id;
      }

      if (!activeBrand) {
        return [];
      }

      const startDate = new Date();
      startDate?.setDate(startDate?.getDate() - days);

      // Get follower growth data
      const { data: followerGrowth } = await supabase?.from('follower_growth')?.select('follower_count, recorded_at')?.eq('brand_id', activeBrand)?.gte('recorded_at', startDate?.toISOString())?.order('recorded_at', { ascending: true });

      // Get engagement data
      const { data: engagementData } = await supabase?.from('post_analytics')?.select('engagement, recorded_at')?.eq('brand_id', activeBrand)?.gte('recorded_at', startDate?.toISOString())?.order('recorded_at', { ascending: true });

      // Combine data by date
      const dataMap = new Map();

      followerGrowth?.forEach(item => {
        const date = new Date(item?.recorded_at)?.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        if (!dataMap?.has(date)) {
          dataMap?.set(date, { date, followers: 0, engagement: 0 });
        }
        const entry = dataMap?.get(date);
        if (entry) {
          entry.followers = item?.follower_count;
        }
      });

      engagementData?.forEach(item => {
        const date = new Date(item?.recorded_at)?.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        if (!dataMap?.has(date)) {
          dataMap?.set(date, { date, followers: 0, engagement: 0 });
        }
        const entry = dataMap?.get(date);
        if (entry) {
          entry.engagement += item?.engagement;
        }
      });

      return Array?.from(dataMap?.values());
    } catch (error) {
      console.error('Error fetching growth data:', error);
      throw error;
    }
  },

  /**
   * Helper to format timestamps
   */
  formatTimestamp(timestamp) {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now - date;
    const diffMins = Math?.floor(diffMs / 60000);
    const diffHours = Math?.floor(diffMs / 3600000);
    const diffDays = Math?.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date?.toLocaleDateString();
  }
};